/**
 * ECharts Bar implementation. Canvas-based. Config-driven theming reads from
 * chartTheme('echarts').echartsTheme. Tooltips are rendered by ECharts'
 * built-in tooltip with custom HTML matching the DrillAwareTooltip shape.
 */

import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { BarChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = BarChartProps & CommonChartProps;

export function EchartsBar({ data, orientation = 'vertical' }: Props): JSX.Element {
  const cfg = chartTheme('echarts');
  const palette = cfg.theme.categoricalPalette;
  const isHorizontal = orientation === 'horizontal';

  const option = useMemo(
    () => ({
      grid: { left: isHorizontal ? 120 : 56, right: 24, top: 16, bottom: 36, containLabel: true },
      tooltip: {
        trigger: 'item',
        backgroundColor: 'transparent',
        borderWidth: 0,
        padding: 0,
        extraCssText: 'box-shadow: none;',
        formatter: (params: { dataIndex: number; data: number; name: string }) => {
          const datum = data[params.dataIndex];
          if (!datum) return '';
          return renderToStaticMarkup(
            <DrillAwareTooltip
              rows={[
                {
                  color: (datum.color as string) ?? palette[params.dataIndex % palette.length],
                  label: params.name,
                  value: params.data,
                },
              ]}
            />
          );
        },
      },
      [isHorizontal ? 'yAxis' : 'xAxis']: {
        type: 'category',
        data: data.map((d) => d.label),
        axisLabel: { color: cfg.theme.textColor, fontSize: 11, fontFamily: cfg.theme.fontFamily },
        axisLine: { lineStyle: { color: cfg.theme.gridColor } },
      },
      [isHorizontal ? 'xAxis' : 'yAxis']: {
        type: 'value',
        axisLabel: { color: cfg.theme.textColor, fontSize: 11, fontFamily: cfg.theme.fontFamily },
        splitLine: { lineStyle: { color: cfg.theme.gridColor, type: 'dashed' } },
      },
      series: [
        {
          type: 'bar',
          data: data.map((d, i) => ({
            value: d.value,
            itemStyle: { color: (d.color as string) ?? palette[i % palette.length], borderRadius: 4 },
          })),
        },
      ],
    }),
    [data, isHorizontal, palette, cfg]
  );

  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />;
}
